import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
    baseRequest,
    ReactiveDropdownSyncController,
    ReactiveSelectionResolver,
    type ReactiveFormLoadPlan,
} from '@fitogest/reactive-form-builder/utils';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { BaseForm } from 'src/app/view/base-form';

import { DashboardService } from 'src/app/service/events/dashboard.service';
import { FormCommonApiService } from 'src/app/service/api/formcommon-api.service';
import { ProductosApiService } from 'src/app/service/api/productos-api';
import { ClientesApiService } from 'src/app/service/api/clientes-api.service';
import { TratamientosApiService } from 'src/app/service/api/tratamientos-api.service';

import { ClienteModel } from 'src/app/models/cliente.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { Uso } from 'src/app/models/productos/uso.model';
import { UserModel } from 'src/app/models/usuarios/user.model';
import { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';

import { TratamientosFormDefinition } from './form-control/form-definition';
import {
    TratamientosFormDataLoader,
    type TratamientosFormDataLoaderContext,
    type TratamientosFormDataLoaderEndpoints,
} from './tratamientos-form-data-loader';
import { TratamientosEditStateHydrator } from './tratamientos-edit-state.hydrator';
import {
    TratamientosCachedProductDetail,
    TratamientosDropdownDataSourceFactory,
} from './tratamientos-dropdown-datasource.factory';
import {
    ReactiveFormChangedField,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { BrowserDiagnosticsService } from 'src/app/common/diagnostics/browser-diagnostics.service';
import { ProductCatalogCacheService } from 'src/app/service/cache/product-catalog-cache.service';

type UnitDropdownOption = { label: string; value: string | null };

type UnidadMedidaApiRow = {
    unidades_de_medida?: string | number | null;
    unidad_medida?: string | number | null;
    unidad?: string | number | null;
    label?: string | number | null;
    value?: string | number | null;
};

const COMMON_UNIT_OPTIONS = ['kg/ha', 'L/ha', 'kg', 'L', '%', 'cc/100 L', 'cc/100L'] as const;

const PRODUCT_CACHE_TTL_MS = 300000;
const PRODUCT_DETAIL_CACHE_TTL_MS = 900000;

@Component({
    selector: 'app-tratamientos-form',
    standalone: false,
    templateUrl: './tratamientos-form.component.html',
    styleUrls: ['./tratamientos-form.component.scss']
})
export class TratamientosFormComponent extends BaseForm<TratamientosModel> implements OnInit {
    public form!: TratamientosFormDefinition;
    public reactiveFormRecordId = '';

    public loadingData: boolean = true;

    /**
     * Stable snapshot used only to bootstrap the builder.
     * It must NOT be replaced with live form changes, otherwise the child builder
     * receives a new previousData input on every edit and re-patches itself.
     */
    public initialPreviousData: TratamientosModel | null = null;

    /**
     * Last runtime snapshot received from the builder.
     * Used for read operations only; not fed back into [previousData].
     */
    private runtimeModelSnapshot: TratamientosModel | null = null;

    private reactiveFormLoadPlan: ReactiveFormLoadPlan | null = null;

    private rawClients: ClienteModel[] = [];

    private rawProducts: ProductosModel[] = [];

    private lastProductOptionsSignature = '';

    private readonly diagnostics = inject(BrowserDiagnosticsService, { optional: true });

    private readonly productCatalogCache = inject(ProductCatalogCacheService);

    private readonly dropdownDataSourceFactory = new TratamientosDropdownDataSourceFactory((product) => {
        return this.loadMagramaDetailForFactory(product);
    });

    private readonly editStateHydrator = new TratamientosEditStateHydrator(this.dropdownDataSourceFactory);

    public constructor(
        public tratamientosApi: TratamientosApiService,
        public formApi: FormCommonApiService,
        public productosApi: ProductosApiService,
        public clientesApi: ClientesApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public dashboardEvents: DashboardService
    ) {
        super(
            route,
            router,
            dashboardEvents,
            tratamientosApi.tratamientosPUT,
            tratamientosApi.tratamientosPOST,
            'tratamientos',
            'Editar Tratamiento',
            'Crear Tratamiento',
            'Duplicar Tratamiento'
        );
    }

    public ngOnInit(): void {
        const scope = this.diagnostics?.start('ng-on-init', 'tratamientos-form', this.getDiagnosticsMetadata());

        this.resetExternalDropdowns();

        this.form = new TratamientosFormDefinition(
            this.getType(),
            this.getModel() as TratamientosModel,
            null
        );

        this.form.model = this.initializeHostModel({
            ...this.form.model,
            ...(this.getModel() as TratamientosModel)
        });

        this.createTratamientosFormLoadPlan();

        this.reactiveFormRecordId = this.resolveReactiveFormRecordId(this.form.model);
        this.initialPreviousData = { ...this.form.model };
        this.runtimeModelSnapshot = { ...this.form.model };
        this.form.setProductosDataSource(this.productosApi.productosDropdown.GET);
        this.form.usosResolver = (producto: ProductosModel | null) => {
            return this.dropdownDataSourceFactory.getUsosForProduct(producto);
        };
        this.form.agentesResolver = (producto: ProductosModel | null, uso: Uso | null) => {
            return this.dropdownDataSourceFactory.getAgentesForProduct(producto, uso);
        };
        this.form.loadUsosData = this.dropdownDataSourceFactory.getLoadData('uso');
        this.form.loadAgentesData = this.dropdownDataSourceFactory.getLoadData('agente');

        this.form.patchUnitsAfterAgentChange = (ctx) => {
            return this.dropdownDataSourceFactory.patchUnitsAfterAgentChange(ctx);
        };
        this.form.patchAppliedDoseAfterNotebookDoseChange = (ctx) => {
            return this.dropdownDataSourceFactory.patchAppliedDoseAfterNotebookDoseChange(ctx);
        };

        this.form.formFields.beforeRepeatableHydration = async ({ previousData }) => {
            await this.prepareRepeatableEditState(previousData);
        };

        void this.initFormData();
        this.softInit(this.getType());
        scope?.end(this.getDiagnosticsMetadata());
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TratamientosModel>): void {
        const modelChanges = this.captureStableValueChange(event.value as TratamientosModel);

        this.applyFormChange(modelChanges as ReactiveFormValue<TratamientosModel>, event.changedField, event.changedFields);
    }

    private applyFormChange(
        formModel: ReactiveFormValue<TratamientosModel>,
        lastChangedFieldName: ReactiveFormChangedField<TratamientosModel>,
        changedFields: readonly string[]
    ): void {
        const scope = this.diagnostics?.start('form-changes', 'tratamientos-form', {
            ...this.getDiagnosticsMetadata(),
            field: String(lastChangedFieldName),
        });
        const changedFieldSet = new Set(changedFields.map((fieldName) => String(fieldName)));

        this.runtimeModelSnapshot = { ...(formModel as TratamientosModel) };
        this.form.tag = lastChangedFieldName;

        if (changedFieldSet.has('id_cliente')) {
            this.postProcessClientes();
            this.refreshReactiveDropdownMappings();
            scope?.end(this.getDiagnosticsMetadata());
            return;
        }

        if (changedFieldSet.has('__repeatable__') || changedFieldSet.has('ids_productos')) {
            this.postProcessProductos();
            scope?.end(this.getDiagnosticsMetadata());
            return;
        }

        this.refreshReactiveDropdownMappings();
        scope?.end(this.getDiagnosticsMetadata());
    }

    private normalizePrimaryKey(value: unknown, primaryKey: string = 'id'): string {
        return ReactiveSelectionResolver.normalizeId(value, { idKey: primaryKey }) ?? '';
    }

    private getRuntimeModelSnapshot(): TratamientosModel {
        const builderValue = this.formBuilderRef?.model?.getRawValue?.() as TratamientosModel | undefined;

        if (builderValue) {
            return builderValue;
        }

        if (this.runtimeModelSnapshot) {
            return this.runtimeModelSnapshot;
        }

        return this.form.model;
    }

    private refreshReactiveDropdownMappings(): void {
        ReactiveDropdownSyncController.refreshMappings({ formBuilder: this.formBuilderRef });
    }

    private mapClientsToOptions(clients: ClienteModel[]): Array<{ label: string; value: ClienteModel }> {
        return clients.map((item) => ({
            label: item.nombre || item.razon_social || '',
            value: item
        }));
    }

    private shouldLoadDistinctAgentes(): boolean {
        return this.form.env.appName === 'hernandorena' && this.form.env.showTratamientosAllProductos;
    }

    private rebuildProductIndexes(): void {
        this.dropdownDataSourceFactory.setProducts(this.rawProducts, this.form.env.showTratamientosAllProductos);
        this.form.setProductIndex(this.dropdownDataSourceFactory.getAllProducts());
    }

    private normalizeOptionLabel(value: unknown): string {
        return String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }


    private getSelectedClientId(): string {
        const currentModel = this.getRuntimeModelSnapshot();

        return this.normalizePrimaryKey(currentModel.id_cliente, 'id');
    }

    private getSelectedProductIds(): Set<string> {
        const currentModel = this.getRuntimeModelSnapshot();

        return new Set(ReactiveSelectionResolver.extractIdList(currentModel.ids_productos));
    }

    private async prepareRepeatableEditState(previousData: TratamientosModel): Promise<void> {
        this.form.model = {
            ...this.form.model,
            ...previousData,
        };
        await this.editStateHydrator.hydrate(previousData);
        this.postProcessProductos();
    }

    private initFormData(): Promise<void> {
        const scope = this.diagnostics?.start('init-form-data', 'tratamientos-form', this.getDiagnosticsMetadata());

        this.loadingData = true;

        return TratamientosFormDataLoader.loadCritical(this.createTratamientosDataLoaderContext())
            .then(() => {
                this.applyLoadedSupportData();
                this.loadingData = false;
                this.finalizeExternalDropdowns();
                this.refreshReactiveDropdownMappings();
                scope?.addMetric('clients', this.rawClients.length, 'items');
                scope?.addMetric('products', this.rawProducts.length, 'items');
                scope?.addMetric('units', this.form.lists.unidadesMedida.filtered.length, 'items');
                scope?.end(this.getDiagnosticsMetadata());

                void this.initDeferredFormData();
            })
            .catch((error: unknown) => {
                scope?.fail(error, this.getDiagnosticsMetadata());
                console.error('Error loading critical tratamientos form data:', error);
                this.loadingData = false;
                this.finalizeExternalDropdowns();
            });
    }

    private initDeferredFormData(): Promise<boolean> {
        return TratamientosFormDataLoader.loadDeferred({
            ...this.createTratamientosDataLoaderContext(),
            setLoadingData: () => {
                // Deferred support data must not close the already-rendered form shell.
            },
        })
            .then((loaded) => {
                this.applyLoadedSupportData();
                this.refreshReactiveDropdownMappings();

                return loaded;
            })
            .catch((error: unknown) => {
                console.error('Error loading deferred tratamientos form data:', error);

                return false;
            });
    }

    private createTratamientosDataLoaderContext(): TratamientosFormDataLoaderContext {
        return {
            form: this.form,
            formType: this.getType(),
            endpoints: this.createTratamientosDataLoaderEndpoints(),
            shouldLoadDistinctAgentes: this.shouldLoadDistinctAgentes(),
            showUnidadesMedidaSIEX: this.form.env.showUnidadesMedidaSIEX,
            isClientFieldVisible: this.isClientFieldVisible(),
            loadProducts: () => this.productCatalogCache.getOrLoad<unknown>(
                this.getProductCacheConfig('productos-dropdown-get'),
                () => baseRequest(this.productosApi.productosDropdown.GET) as Promise<unknown>
            ),
            setClients: (clients) => {
                this.rawClients = clients;
            },
            setProducts: (products) => {
                this.rawProducts = products;
            },
            setDistinctAgentes: (agentes, isEnabled) => {
                this.dropdownDataSourceFactory.setDistinctAgentes(agentes, isEnabled);
            },
            setUnits: (units) => {
                this.populateUnitOptions(units as UnidadMedidaApiRow[]);
            },
            setLoadingData: (loading) => {
                this.loadingData = loading;
            },
            loadPlan: this.reactiveFormLoadPlan,
        };
    }

    private createTratamientosDataLoaderEndpoints(): TratamientosFormDataLoaderEndpoints {
        return {
            clientesGET: this.clientesApi.clientesGET,
            productosGET: this.productosApi.productosDropdown.GET,
            mgAgentes: this.formApi.mgAgentes,
            mgUnidadesMedida: this.formApi.mgUnidadesMedida,
        };
    }

    private createTratamientosFormLoadPlan(): void {
        this.reactiveFormLoadPlan = this.createReactiveFormLoadPlan(this.form.formFields);
    }

    private isClientFieldVisible(): boolean {
        return this.form.env.hasClients && this.form.env.userCanSeeClientes && this.getType() === FormType.DUPLICATION;
    }

    private applyLoadedSupportData(): void {
        this.rebuildProductIndexes();

        const clientOptions = this.mapClientsToOptions(this.rawClients);

        this.form.lists.clientes.filtered = [...clientOptions];
        this.form.lists.clientes.values = [...clientOptions];

        this.form.lists.productosAll.filtered = this.dropdownDataSourceFactory.getAllProductOptions();
        this.form.lists.productosAll.values = [...this.form.lists.productosAll.filtered];

        this.postProcessClientes();
        this.applyInitialState();
        this.postProcessProductos();
    }

    private populateUnitOptions(units: UnidadMedidaApiRow[]): void {
        const optionByKey = new Map<string, UnitDropdownOption>();

        for (const unit of COMMON_UNIT_OPTIONS) {
            this.addUnitOption(optionByKey, unit);
        }

        for (const unit of units) {
            this.addUnitOption(optionByKey, this.extractUnitLabel(unit));
        }

        const unitOptions: UnitDropdownOption[] = [
            { label: '...', value: null },
            ...[...optionByKey.values()].sort((firstOption, secondOption) => {
                return firstOption.label.localeCompare(secondOption.label);
            })
        ];

        this.form.lists.unidadesMedida.filtered = unitOptions;
        this.form.lists.unidadesMedida.values = [...unitOptions];
    }

    private addUnitOption(optionByKey: Map<string, UnitDropdownOption>, rawUnit: unknown): void {
        const label = String(rawUnit ?? '').trim();
        const key = this.normalizeUnitOptionKey(label);

        if (label === '' || key === '' || optionByKey.has(key)) {
            return;
        }

        optionByKey.set(key, { label, value: label });
    }

    private extractUnitLabel(unit: UnidadMedidaApiRow): string {
        return String(
            unit.unidades_de_medida
            ?? unit.unidad_medida
            ?? unit.unidad
            ?? unit.label
            ?? unit.value
            ?? ''
        ).trim();
    }

    private normalizeUnitOptionKey(value: unknown): string {
        return String(value ?? '')
            .trim()
            .replace(/\s+/g, '')
            .toLowerCase();
    }

    private postProcessClientes(): void {
        const user = StorageManager.getUser() as UserModel;

        const source = [...this.rawClients];

        let filteredClients = source;

        if (!this.form.env.showTareasMto) {
            if (!(this.form.env.appName === 'sequiador' && user.rol === 'admin')) {
                filteredClients = source.filter((item) => {
                    return [item.id_usuario_asignado, item.id_usuario_asignado2].includes(user.id);
                });
            }
        }

        const selectedClientId = this.getSelectedClientId();

        if (selectedClientId !== '') {
            const selectedClient = source.find((item) => {
                return String(item.id ?? '') === selectedClientId;
            }) ?? null;

            if (selectedClient && !filteredClients.some((item) => String(item.id ?? '') === selectedClientId)) {
                filteredClients = [selectedClient, ...filteredClients];
            }
        }

        filteredClients = [...filteredClients].sort((a, b) => {
            return String(a.nombre ?? a.razon_social ?? '').localeCompare(
                String(b.nombre ?? b.razon_social ?? '')
            );
        });

        this.form.lists.clientes.filtered = this.mapClientsToOptions(filteredClients);

        this.form.lists.clientes.values = [...this.form.lists.clientes.filtered];

        if (this.getType() === FormType.CREATION) {
            const currentClientId = String(StorageManager.getClient()?.id ?? '');

            const currentClient = filteredClients.find((item) => {
                return String(item.id ?? '') === currentClientId;
            }) ?? null;

            if (currentClient) {
                this.syncSelectedClient(currentClient);
                this.form.model.id_cliente = String(currentClient.id ?? '');
                return;
            }
        }

        const selectedClient = filteredClients.find((item) => {
            return String(item.id ?? '') === selectedClientId;
        }) ?? null;

        if (selectedClient) {
            this.syncSelectedClient(selectedClient);
        }
    }

    private syncSelectedClient(client: ClienteModel): void {
        ReactiveDropdownSyncController.syncSelection<TratamientosModel, ClienteModel>({
            formBuilder: this.formBuilderRef,
            fieldName: 'id_cliente',
            dropdown: this.form.lists.clientes,
            rawValue: client,
            emit: false,
            configuration: { idKey: 'id' },
        });
    }

    private postProcessProductos(): void {
        const selectedProductIds = this.getSelectedProductIds();
        const selectedSignature = [...selectedProductIds].sort().join('|');
        const optionsSignature = [
            this.rawProducts.length,
            this.form.env.showTratamientosAllProductos ? 'all' : 'limited',
            selectedSignature
        ].join(':');

        if (optionsSignature === this.lastProductOptionsSignature) {
            return;
        }

        const scope = this.diagnostics?.start('post-process-productos', 'tratamientos-form', this.getDiagnosticsMetadata());
        const productOptions = this.dropdownDataSourceFactory.getSelectableProductOptions(selectedProductIds);

        this.form.lists.productos.filtered = productOptions;
        this.form.lists.productos.values = [...productOptions];
        this.lastProductOptionsSignature = optionsSignature;

        scope?.addMetric('sourceProducts', this.rawProducts.length, 'items');
        scope?.addMetric('filteredProducts', productOptions.length, 'items');
        scope?.addMetric('selectedProducts', selectedProductIds.size, 'items');
        scope?.end(this.getDiagnosticsMetadata());
    }

    private applyInitialState(): void {
        if (this.getType() === FormType.CREATION) {
            const user = StorageManager.getUser() as UserModel;
            const clientId = user.tipo === 'comunero'
                ? String(user.id_cliente ?? '')
                : String(StorageManager.getClient()?.id ?? '');

            if (clientId !== '') {
                this.form.model.id_cliente = clientId;
            }
        }

        if (this.getType() === FormType.DUPLICATION) {
            delete this.form.model.id;
        }
    }

    private loadMagramaDetailForFactory(product: ProductosModel): Promise<TratamientosCachedProductDetail> {
        const registro = String(product.num_registro ?? '').trim();

        return this.productCatalogCache.getOrLoad<TratamientosCachedProductDetail>(
            this.getProductCacheConfig('magrama-detalle-producto', { registro }, PRODUCT_DETAIL_CACHE_TTL_MS),
            () => this.fetchProductDetailFromApi(registro, product)
        );
    }

    private fetchProductDetailFromApi(
        registro: string,
        product: ProductosModel
    ): Promise<TratamientosCachedProductDetail> {
        const scope = this.diagnostics?.start('fetch-product-detail', 'tratamientos-form', {
            ...this.getDiagnosticsMetadata(),
            registro,
            productId: product.id ?? null,
        });

        return this.formApi.magramaDetalleProducto
            .toPromise({
                referencia: `'${registro}'`
            } as never)
            .then((response: Uso[]) => {
                const detail = this.mapProductDetailResponse(response ?? []);

                scope?.addMetric('usos', detail.usos.length, 'items');
                scope?.addMetric('agentes', detail.agentes.length, 'items');
                scope?.end(this.getDiagnosticsMetadata());

                return detail;
            })
            .catch((error: unknown) => {
                scope?.fail(error, this.getDiagnosticsMetadata());

                return {
                    usos: [{ label: 'Uso...', value: null }],
                    agentes: []
                };
            });
    }

    private mapProductDetailResponse(response: Uso[]): TratamientosCachedProductDetail {
        const seenUsoLabels = new Set<string>();
        const seenAgenteUsageLabels = new Set<string>();

        const usos = response
            .map((item) => ({
                label: item.uso ?? '',
                value: item
            }))
            .filter((item) => {
                const usageLabel = this.normalizeOptionLabel(item.label);

                if (usageLabel === '' || seenUsoLabels.has(usageLabel)) {
                    return false;
                }

                seenUsoLabels.add(usageLabel);
                return true;
            });

        const agentes = response
            .map((item) => {
                const normalized: Uso = {
                    ...item,
                    minDosis: item.dosis1 ?? 0,
                    maxDosis: item.dosis2 ?? 0,
                    dosis_min: item.dosis1 ?? 0,
                    dosis_max: item.dosis2 ?? 0,
                    isInvalid: false
                };

                return {
                    label: normalized.agente ?? '',
                    value: normalized
                };
            })
            .filter((item) => {
                const usageLabel = this.normalizeOptionLabel(item.value?.uso ?? '');
                const agentLabel = this.normalizeOptionLabel(item.label);
                const uniqueKey = `${usageLabel}::${agentLabel}`;

                if (agentLabel === '' || seenAgenteUsageLabels.has(uniqueKey)) {
                    return false;
                }

                seenAgenteUsageLabels.add(uniqueKey);
                return true;
            });

        return {
            usos: [{ label: 'Uso...', value: null }, ...usos],
            agentes
        };
    }

    private getProductCacheConfig(
        name: string,
        params: Record<string, unknown> = {},
        ttlMs: number = PRODUCT_CACHE_TTL_MS
    ): { namespace: string; name: string; ttlMs: number; params: Record<string, unknown>; userScope: string; clientScope: string } {
        return {
            namespace: 'tratamientos-form',
            name,
            ttlMs,
            params,
            userScope: String(StorageManager.getUser()?.id ?? 'anonymous'),
            clientScope: String(StorageManager.getClient()?.id ?? 'no-client')
        };
    }

    private getDiagnosticsMetadata(): Record<string, unknown> {
        return {
            formType: this.getType(),
            requestType: this.form?.formType ?? null,
            loadingData: this.loadingData,
            externalDropdownsReady: this.externalDropdownsReady,
            rawClients: this.rawClients.length,
            rawProducts: this.rawProducts.length,
            cachedProductDetails: this.dropdownDataSourceFactory.getCachedProductDetailCount(),
        };
    }
}
